import { useEffect, useSyncExternalStore } from 'react';

/** Canal por el que se comparte; va en utm_medium. */
export type ShareMedium = 'whatsapp' | 'x' | 'copy' | 'telegram' | 'facebook' | 'linkedin';

export const DEFAULT_SHARE_TEXT = 'Conoce cómo votan tus diputados en el Congreso de Chile';

/**
 * Devuelve la URL con utm_source=share y utm_medium=<canal>.
 * Reemplaza los utm_* previos (p. ej. si la persona llegó por un enlace con UTM), salvo utm_content;
 * conserva el resto de los parámetros y el hash.
 */
export function withShareUtm(url: string, medium: ShareMedium): string {
  try {
    const parsed = new URL(url);
    // utm_content se conserva: lo usan los experimentos (p. ej. con_og) para distinguir enlaces.
    Array.from(parsed.searchParams.keys())
      .filter((key) => key.startsWith('utm_') && key !== 'utm_content')
      .forEach((key) => parsed.searchParams.delete(key));
    parsed.searchParams.set('utm_source', 'share');
    parsed.searchParams.set('utm_medium', medium);
    return parsed.toString();
  } catch {
    return url;
  }
}

// Texto de compartir de la ficha abierta. El menú de compartir vive en el layout,
// fuera de la página, así que cada ficha lo publica aquí mientras está montada.
let currentShareText: string | null = null;
const listeners = new Set<() => void>();

function setShareText(text: string | null): void {
  if (currentShareText === text) {
    return;
  }
  currentShareText = text;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Lo usan las fichas: fija el texto con el dato concreto mientras están montadas. */
export function usePublishShareText(text: string | null | undefined): void {
  useEffect(() => {
    setShareText(text || null);
    return () => setShareText(null);
  }, [text]);
}

/** Lo usan los menús de compartir: texto de la ficha abierta o el genérico. */
export function useShareText(): string {
  return useSyncExternalStore(subscribe, () => currentShareText) || DEFAULT_SHARE_TEXT;
}

function clip(value: string, max: number): string {
  const clean = value.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) {
    return clean;
  }
  const cut = clean.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

const VOTE_TYPE_LABEL: Record<string, string> = {
  unica: 'votación única',
  general: 'votación en general',
  particular: 'votación en particular',
  admisibilidad: 'votación de admisibilidad',
};

// Mismo criterio que front-end/api/_meta/patterns.js (el título de las fichas para bots).
function voteTypeLabel(voteType?: string | null): string {
  const key = (voteType || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  return VOTE_TYPE_LABEL[key] || 'votación';
}

/** Quita del título un resultado que ya trae, p. ej. "Proyecto X (Rechazado)". */
function stripTrailingResult(title: string): string {
  return title.replace(/\s*\((aprobad[oa]|rechazad[oa])[^)]*\)\s*$/i, '').trim() || title;
}

/** «Aprobado» -> «aprobada»: el resultado se pega a la «votación», no al proyecto. */
function voteResultWord(resultado?: string | null): string {
  const r = (resultado || '').trim().toLowerCase();
  if (r.startsWith('aprobad')) {
    return ' aprobada';
  }
  if (r.startsWith('rechazad')) {
    return ' rechazada';
  }
  return '';
}

/**
 * Texto para compartir una votación: título, tipo de votación, resultado y conteo.
 * El resultado va como adjetivo de la votación («votación única rechazada»), nunca como
 * «Proyecto X: Rechazado»: muchas votaciones son de un artículo, una enmienda del Senado,
 * una observación del Presidente o una partida, y el rechazo no es el de la ley. Con el
 * resultado va el conteo, porque en quórum calificado o con empates 88 a 28 puede ser un rechazo.
 */
export function voteShareText(vote: {
  title?: string | null;
  voteType?: string | null;
  resultado?: string | null;
  si?: number | null;
  no?: number | null;
  abstenciones?: number | null;
}): string {
  const title = clip(stripTrailingResult(vote.title || 'Votación en la Cámara'), 140);
  // Tres ceros es un dato ausente (hay una votacion antigua con 42 pareos y 0/0/0), no un conteo.
  const { si, no, abstenciones } = vote;
  const counts =
    si != null && no != null && abstenciones != null && si + no + abstenciones > 0
      ? ` (${si} a favor, ${no} en contra, ${plural(abstenciones, 'abstención', 'abstenciones')})`
      : '';
  return `${title}: ${voteTypeLabel(vote.voteType)}${voteResultWord(vote.resultado)}${counts}. Mira cómo votó cada diputado:`;
}

/** Texto para compartir la ficha de un diputado. */
export function deputyShareText(name?: string | null, party?: string | null): string | null {
  if (!name) {
    return null;
  }
  const label = party ? `${name} (${party})` : name;
  return `${label}: cómo ha votado en la Cámara y su asistencia a las votaciones:`;
}

/** Texto para compartir un proyecto (boletín). */
export function bulletinShareText(title?: string | null, boletin?: string | null): string | null {
  if (!title && !boletin) {
    return null;
  }
  const head = title ? clip(title, 140) : String(boletin);
  return `${head}${title && boletin ? ` (${boletin})` : ''}: cómo votó cada diputado:`;
}

/** Tipo e id de la ficha a partir de la ruta, para el evento content_shared. */
export function shareContentFromUrl(url: string): { type: string; id: string } {
  let pathname = '/';
  try {
    pathname = new URL(url).pathname;
  } catch {
    /* url relativa o invalida: se trata como el sitio */
  }
  if (/^\/semana\/?$/.test(pathname)) {
    return { type: 'week', id: 'actual' };
  }
  const m = /^\/(votaciones|deputy|boletines|semana)\/([^/]+)/.exec(pathname);
  if (!m || m[2] === 'comparar') {
    return { type: 'site', id: pathname };
  }
  const type = m[1] === 'votaciones' ? 'vote' : m[1] === 'deputy' ? 'deputy' : m[1] === 'semana' ? 'week' : 'bulletin';
  let id = m[2];
  try {
    id = decodeURIComponent(id);
  } catch {
    /* se deja como viene */
  }
  return { type, id };
}
